import { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import './SearchableDropdown.scss';

export default function SearchableDropdown({
  clusterListProp = [],
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Select a cluster...'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Use controlled value from parent
  const selectedCluster = value;

  // Filter clusters based on search term (client-side filtering)
  const filteredClusters = clusterListProp.filter(cluster =>
    cluster.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cluster) => {
    if (onChange) {
      onChange(cluster);
    }
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange('');
    }
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
      <div className="dropdown-page">
        <div className="dropdown-page__container">

          <div className="dropdown" ref={dropdownRef}>
            {/* Dropdown Button */}
            <div className="dropdown__trigger">
              <button
                type="button"
                onClick={toggleDropdown}
                className={`dropdown__button ${disabled ? 'dropdown__button--disabled' : ''}`}
                disabled={disabled}
              >
                <span className={`dropdown__selected-text ${!selectedCluster ? 'dropdown__selected-text--placeholder' : ''}`}>
                  {selectedCluster || placeholder}
                </span>
                <div className="dropdown__actions">
                  {selectedCluster && !disabled && (
                    <button
                      onClick={handleClear}
                      className="dropdown__clear-btn"
                      type="button"
                    >
                      <X className="dropdown__clear-btn" />
                    </button>
                  )}
                  <ChevronDown
                    className={`dropdown__chevron ${isOpen ? 'dropdown__chevron--rotated' : ''}`}
                  />
                </div>
              </button>
            </div>

            {/* Dropdown Menu */}
            {isOpen && !disabled && (
              <div className="dropdown__menu">
                {/* Search Input */}
                <div className="dropdown__search-container">
                  <div className="dropdown__search-wrapper">
                    <Search className="dropdown__search-icon" />
                    <input
                      type="text"
                      placeholder="Search clusters..."
                      value={searchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow only alphanumeric characters
                        if (/^[A-Za-z0-9]*$/.test(value) || value === '') {
                          setSearchTerm(value);
                        }
                      }}
                      className="dropdown__search-input"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Results List */}
                <div className="dropdown__results">
                  {/* Show all clusters or filtered results */}
                  {filteredClusters.length > 0 ? (
                    filteredClusters.map((cluster, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelect(cluster)}
                        className={`dropdown__item ${selectedCluster === cluster ? 'dropdown__item--selected' : ''}`}
                        type="button"
                      >
                        <span>{cluster}</span>
                      </button>
                    ))
                  ) : searchTerm.length > 0 ? (
                    <div className="dropdown__no-results">
                      No clusters found matching "{searchTerm}"
                    </div>
                  ) : clusterListProp.length === 0 ? (
                    <div className="dropdown__no-results">
                      No clusters available
                    </div>
                  ) : (
                    <div className="dropdown__no-results">
                      Start typing to search clusters
                    </div>
                  )}
                </div>

                {/* Results Count */}
                {filteredClusters.length > 0 && (
                  <div className="dropdown__footer">
                    {filteredClusters.length} result{filteredClusters.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
  );
}